import type { Resource } from '@medplum/fhirtypes';
import type { ReactNode } from 'react';

import { FieldStateMarker } from '../../components/FieldStateMarker';
import { datatypeDisplay } from '../../components/displays';
import type { FhirView, RenderContext, Slots } from '../../core/view-contract';
import type { ChoiceValue } from '../../resolvers/choice';
import {
  isResolvedFieldVisible,
  resolveChoiceField,
  resolveField,
  resolvePrimitiveField,
} from '../../resolvers/field-state';
import {
  type SchemaElement,
  humanizeElementName,
  topLevelElements,
} from '../../schema';

/**
 * Schema-driven fallback for resource types with no dedicated component.
 * Walks the R4 manifest for the type — spec-ordered, spec-typed — resolving
 * every element through the field-state layer, so absent/withheld/DAR
 * semantics hold even for resources nobody wrote a view for.
 */
export const GenericView: FhirView = ({ data, slots, ctx }) => (
  <div data-view="generic" className="rounded-sm border p-3">
    <div className="font-medium">
      {data.resourceType}
      {data.id ? (
        <span className="font-normal text-muted-foreground"> {data.id}</span>
      ) : null}
    </div>
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
      {topLevelElements(data.resourceType).map(([path, element]) => (
        <GenericFieldRow
          key={path}
          path={path}
          element={element}
          data={data}
          slots={slots}
          ctx={ctx}
        />
      ))}
    </dl>
    {ctx.debug && (
      <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
    )}
  </div>
);

/** ChoiceValue arm → spec datatype name, for display dispatch. */
const CHOICE_ARM_TYPE: Record<ChoiceValue['type'], string> = {
  quantity: 'Quantity',
  codeableConcept: 'CodeableConcept',
  coding: 'Coding',
  period: 'Period',
  range: 'Range',
  ratio: 'Ratio',
  humanName: 'HumanName',
  identifier: 'Identifier',
  annotation: 'Annotation',
  attachment: 'Attachment',
  reference: 'Reference',
  dateTime: 'dateTime',
  date: 'date',
  string: 'string',
  number: 'decimal',
  boolean: 'boolean',
  other: '',
  absent: '',
};

function GenericFieldRow({
  path,
  element,
  data,
  slots,
  ctx,
}: {
  path: string;
  element: SchemaElement;
  data: Resource;
  slots: Slots<Resource>;
  ctx: RenderContext;
}): ReactNode {
  if (element.t === 'BackboneElement') return null;

  let content: ReactNode;
  if (element.t === 'Reference') {
    // The walker already rendered these (or stubbed misses) — place its work.
    const slot = (slots as Record<string, ReactNode>)[path];
    if (slot === undefined) return null;
    content = slot;
  } else if (element.t === 'choice') {
    const field = resolveChoiceField({
      resource: data,
      base: path,
      access: ctx.access,
    });
    if (!isResolvedFieldVisible(field, 'when-present-or-withheld', 'read')) {
      return null;
    }
    content =
      'value' in field && field.value.type !== 'absent' ? (
        datatypeDisplay(CHOICE_ARM_TYPE[field.value.type], field.value.value)
      ) : (
        <FieldStateMarker
          state={
            field.state.kind === 'present'
              ? { kind: 'not-recorded' }
              : field.state
          }
        />
      );
  } else {
    const value = (data as unknown as Record<string, unknown>)[path];
    const field = element.a
      ? resolveField({
          resource: data,
          path,
          value: value as readonly unknown[] | undefined,
          access: ctx.access,
        })
      : resolvePrimitiveField({
          resource: data,
          path,
          value,
          access: ctx.access,
        });
    if (!isResolvedFieldVisible(field, 'when-present-or-withheld', 'read')) {
      return null;
    }
    content =
      'value' in field ? (
        element.a ? (
          <span className="flex flex-col">
            {(field.value as readonly unknown[]).map((item, index) => (
              <span key={index}>{datatypeDisplay(element.t, item)}</span>
            ))}
          </span>
        ) : (
          datatypeDisplay(element.t, field.value)
        )
      ) : (
        <FieldStateMarker state={field.state} />
      );
  }

  if (content === null) return null;
  return (
    <>
      <dt className="text-muted-foreground">{humanizeElementName(path)}</dt>
      <dd>{content}</dd>
    </>
  );
}
